import React from 'react';
import { useParams, Navigate, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { reviewService, IParticipant, IReviewPoll } from '../service/review.service';
import { teamService } from '../../team/service/team.service';
import useAuth from '../../auth/feature/auth';
import { authService, IUserWebhook } from '../../auth/service/auth.service';
import { ChatSidebar, ChatToggleButton } from '../components/chat-sidebar.component';
import { MeetingStatusPill } from '../components/meeting-status.component';
import { useNotifications } from '../hooks/use-notifications';
import { usePushSubscription } from '../hooks/use-push-subscription';
import { useReviewSocket } from '../hooks/use-review-socket';
import { InviteModal } from '../../common/components/invite-modal.component';
import { InviteTracker } from '../components/invite-tracker.component';
import { UpgradeModal } from '../../common/components/upgrade-modal.component';

export const ReviewAdminPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  useReviewSocket(slug);
  usePushSubscription({ participantId: null, participantToken: null, userId: user?.id || null, slug: slug || '' });
  const [chatOpen, setChatOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const [copied, setCopied] = React.useState(false);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [copiedPassword, setCopiedPassword] = React.useState(false);
  const [dragId, setDragId] = React.useState<number | null>(null);
  const [dragOverId, setDragOverId] = React.useState<number | null>(null);
  const [editingUrl, setEditingUrl] = React.useState(false);
  const [urlInput, setUrlInput] = React.useState('');
  const [editingLabel, setEditingLabel] = React.useState(false);
  const [labelInput, setLabelInput] = React.useState('');
  const [editingPassword, setEditingPassword] = React.useState(false);
  const [passwordInput, setPasswordInput] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [upgradeMessage, setUpgradeMessage] = React.useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  const { data: review, isLoading } = useQuery<IReviewPoll | void>({
    queryKey: ['review-poll', slug],
    queryFn: () => reviewService.pollReview(slug!),
    refetchInterval: false,
    enabled: !!slug
  });

  const { data: isAdmin, isLoading: adminCheckLoading } = useQuery<boolean | void>({
    queryKey: ['is-admin', slug],
    queryFn: () => reviewService.isUserAdmin(slug!),
    enabled: !!slug && !!user
  });

  const isOwner = user?.id === (review as IReviewPoll)?.adminId;

  const adminName = user?.email ? `Admin (${user.email.split('@')[0]})` : 'Admin';

  const { unreadCount } = useNotifications({
    review: (review as IReviewPoll) || null,
    myParticipantId: null,
    myName: adminName,
    isAdmin: true,
    slug: slug || '',
    chatOpen
  });

  const { t } = useTranslation();

  const { data: userTeams } = useQuery({
    queryKey: ['my-teams'],
    queryFn: () => teamService.getMyTeams(),
    enabled: isOwner
  });

  const { mutate: addTeam } = useMutation({
    mutationFn: (teamId: number) => reviewService.addTeamToReview(slug!, teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-poll', slug] });
      toast.success(t('review.teamAdded'));
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || t('review.teamAddFailed'));
    }
  });

  const { mutate: removeTeam } = useMutation({
    mutationFn: (teamId: number) => reviewService.removeTeamFromReview(slug!, teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-poll', slug] });
      toast.success(t('review.teamRemoved'));
    }
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['review-poll', slug] });

  const { mutate: setStatus } = useMutation({
    mutationFn: ({
      participantId,
      status
    }: {
      participantId: number;
      status: string;
    }) => reviewService.setParticipantStatus(slug!, participantId, status),
    onSuccess: invalidate
  });

  const { mutate: remove } = useMutation({
    mutationFn: (participantId: number) =>
      reviewService.removeParticipant(slug!, participantId),
    onSuccess: invalidate
  });

  const { mutate: reorder } = useMutation({
    mutationFn: (order: number[]) => reviewService.reorderParticipants(slug!, order),
    onSuccess: invalidate
  });

  const [editingTitle, setEditingTitle] = React.useState(false);
  const [titleInput, setTitleInput] = React.useState('');

  const { mutate: saveTitle, isPending: titlePending } = useMutation({
    mutationFn: (title: string) => reviewService.updateReviewTitle(slug!, title),
    onSuccess: () => {
      invalidate();
      setEditingTitle(false);
      toast.success(t('review.titleUpdated'));
    },
    onError: () => toast.error(t('review.titleUpdateFailed'))
  });

  const [showEndModal, setShowEndModal] = React.useState(false);
  const [showClearAllModal, setShowClearAllModal] = React.useState(false);
  const [showReinstateAllModal, setShowReinstateAllModal] = React.useState(false);
  const [confirmReinstate, setConfirmReinstate] = React.useState<IParticipant | null>(null);
  const [confirmRemove, setConfirmRemove] = React.useState<IParticipant | null>(null);

  const { mutate: endReview } = useMutation({
    mutationFn: () => reviewService.deleteReview(slug!),
    onSuccess: () => {
      toast.success(t('review.reviewEnded2'));
      window.location.href = '/dashboard';
    }
  });

  const { mutate: clearCompleted } = useMutation({
    mutationFn: () => reviewService.clearCompleted(slug!),
    onSuccess: () => {
      invalidate();
      setShowClearAllModal(false);
      toast.success(t('review.clearedCompleted'));
    }
  });

  const { mutate: reinstateAll } = useMutation({
    mutationFn: () => reviewService.reinstateAll(slug!),
    onSuccess: () => {
      invalidate();
      setShowReinstateAllModal(false);
      toast.success(t('review.reinstatedAll'));
    }
  });

  const { mutate: toggleMeeting } = useMutation({
    mutationFn: () => reviewService.toggleMeetingStarted(slug!),
    onSuccess: () => invalidate()
  });

  const { mutate: saveMeetingUrl } = useMutation({
    mutationFn: (url: string) =>
      reviewService.updateMeetingUrl(slug!, url || undefined),
    onSuccess: () => {
      setEditingUrl(false);
      invalidate();
      toast.success(t('review.meetingLinkUpdated'));
    }
  });

  const { mutate: saveLinkLabel } = useMutation({
    mutationFn: (label: string) =>
      reviewService.updateMeetingLinkLabel(slug!, label || undefined),
    onSuccess: () => {
      setEditingLabel(false);
      invalidate();
    }
  });

  const { data: passwordData } = useQuery({
    queryKey: ['review-password', slug],
    queryFn: () => reviewService.getReviewPassword(slug!),
    enabled: !!slug && !!isAdmin
  });

  const { mutate: savePassword } = useMutation({
    mutationFn: (pw: string) =>
      reviewService.updateReviewPassword(slug!, pw || undefined),
    onSuccess: () => {
      setEditingPassword(false);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['review-password', slug] });
      toast.success(t('review.passwordUpdated'));
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || '';
      if (msg.toLowerCase().includes('pro')) {
        setUpgradeMessage(t('review.passwordProRequired'));
      } else {
        toast.error(msg || t('review.passwordUpdateFailed'));
      }
    }
  });

  const copyLink = () => {
    const url = `${window.location.origin}/review/${slug}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDragStart = (id: number) => {
    setDragId(id);
  };

  const handleDragOver = (e: React.DragEvent, id: number) => {
    e.preventDefault();
    setDragOverId(id);
  };

  const handleDrop = (
    e: React.DragEvent,
    targetId: number,
    participants: IParticipant[]
  ) => {
    e.preventDefault();
    if (dragId === null || dragId === targetId) {
      setDragId(null);
      setDragOverId(null);
      return;
    }
    const ids = participants.map((p) => p.id);
    const fromIdx = ids.indexOf(dragId);
    const toIdx = ids.indexOf(targetId);
    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, dragId);
    reorder(ids);
    setDragId(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => {
    setDragId(null);
    setDragOverId(null);
  };

  // Touch drag support for mobile (long-press to initiate)
  const cardRefsMap = React.useRef<Map<number, HTMLDivElement>>(new Map());
  const touchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTouchDragging = React.useRef(false);
  const waitingRef = React.useRef<IParticipant[]>([]);
  const touchStartPos = React.useRef<{ x: number; y: number } | null>(null);

  // Keep waitingRef in sync
  React.useEffect(() => {
    waitingRef.current = review ? review.participants.filter((p: IParticipant) => p.status === 'waiting') : [];
  }, [review]);

  // Attach native touch listeners with { passive: false } to prevent scroll and context menu
  React.useEffect(() => {
    const moveHandler = (e: TouchEvent) => {
      // If drag is active, prevent scroll and track position
      if (isTouchDragging.current) {
        e.preventDefault();
        const touch = e.touches[0];
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        if (!el) return;
        cardRefsMap.current.forEach((ref, pid) => {
          if (ref && ref.contains(el as Node)) {
            setDragOverId(pid);
          }
        });
        return;
      }
      // If waiting for long-press, only cancel if moved more than 10px
      if (touchTimerRef.current && touchStartPos.current) {
        const touch = e.touches[0];
        const dx = Math.abs(touch.clientX - touchStartPos.current.x);
        const dy = Math.abs(touch.clientY - touchStartPos.current.y);
        if (dx > 10 || dy > 10) {
          clearTimeout(touchTimerRef.current);
          touchTimerRef.current = null;
        }
      }
    };
    // Prevent browser context menu on long-press
    const contextHandler = (e: Event) => {
      if (isTouchDragging.current || touchTimerRef.current) e.preventDefault();
    };
    // Prevent Safari from stealing touch on draggable cards, but allow buttons/inputs
    const startHandler = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target?.closest?.('[data-draggable]') && !target?.closest?.('button, a, input, select, textarea')) {
        e.preventDefault();
      }
    };
    document.addEventListener('touchstart', startHandler, { passive: false });
    document.addEventListener('touchmove', moveHandler, { passive: false });
    document.addEventListener('contextmenu', contextHandler, { passive: false });
    return () => {
      document.removeEventListener('touchstart', startHandler);
      document.removeEventListener('touchmove', moveHandler);
      document.removeEventListener('contextmenu', contextHandler);
    };
  }, []);

  const handleTouchStart = (id: number, e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    isTouchDragging.current = false;
    touchTimerRef.current = setTimeout(() => {
      isTouchDragging.current = true;
      setDragId(id);
      if (navigator.vibrate) navigator.vibrate(30);
    }, 200);
  };

  const handleTouchMove = () => {
    // Movement handling is done in the native listener above
  };

  const handleTouchEnd = () => {
    touchStartPos.current = null;
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
    if (!isTouchDragging.current) return;
    isTouchDragging.current = false;

    if (dragId === null || dragOverId === null || dragId === dragOverId) {
      setDragId(null);
      setDragOverId(null);
      return;
    }
    const participants = waitingRef.current;
    const ids = participants.map((p) => p.id);
    const fromIdx = ids.indexOf(dragId);
    const toIdx = ids.indexOf(dragOverId);
    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, dragId);
    reorder(ids);
    setDragId(null);
    setDragOverId(null);
  };

  if (isLoading || !review || adminCheckLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#1D1D1E] border-t-transparent rounded-full" />
      </div>
    );
  }

  // Only admins and co-admins can access admin controls
  if (!isAdmin) {
    return <Navigate to={`/review/${slug}`} replace />;
  }

  const getInitials = (name: string) => {
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const waiting = review.participants.filter((p) => p.status === 'waiting').sort((a, b) => a.position - b.position);
  const active = review.participants.filter((p) => p.status === 'active');
  const done = review.participants.filter((p) => p.status === 'done');
  const meetingHref = review.meetingUrl && !/^https?:\/\//i.test(review.meetingUrl)
    ? `https://${review.meetingUrl}`
    : review.meetingUrl;

  return (
    <div className="max-w-[600px] mx-auto py-[32px] md:py-[48px]">
      {(review as any).company && (
        <div className="flex items-center justify-center gap-[8px] mb-[16px]">
          {(review as any).company.logoUrl && <img src={(review as any).company.logoUrl} alt="" className="w-[20px] h-[20px] rounded-[4px] object-cover" />}
          <span className="font-body text-[13px] font-medium" style={{ color: (review as any).company.primaryColor || '#0ea5c9' }}>{(review as any).company.name}</span>
        </div>
      )}
      <div className="flex items-center justify-between mb-[8px]">
        <Link to="/dashboard" className="inline-flex items-center gap-[4px] font-body text-[13px] font-medium text-[#818185] hover:text-base-black transition-colors">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          {t('header.dashboard')}
        </Link>
        <div className="flex items-center gap-[8px]">
          {isOwner && (
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="inline-flex items-center justify-center w-[32px] h-[32px] rounded-full border border-gray-500 bg-gray-400 text-gray-900 hover:bg-[#E0E0E3] hover:text-base-black transition-all"
              title="Settings"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
          )}
          <Link
            to={`/review/${slug}`}
            className="inline-flex items-center gap-[6px] h-[32px] px-[14px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[12px] font-medium text-gray-900 dark:text-[#A0A0A3] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] hover:text-base-black dark:hover:text-white transition-all"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
            </svg>
            {t('review.viewAsParticipant')}
          </Link>
        </div>
      </div>
      <div className="mb-[24px]">
        {editingTitle ? (
          <div className="flex gap-[8px] mb-[6px]">
            <input
              value={titleInput}
              onChange={(e) => setTitleInput(e.target.value)}
              className="flex-1 h-[44px] px-[16px] text-[20px] font-display font-bold text-base-black bg-white border border-gray-700 rounded-[24px] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && titleInput.trim()) saveTitle(titleInput.trim()); if (e.key === 'Escape') setEditingTitle(false); }}
            />
            <button
              type="button"
              onClick={() => { if (titleInput.trim()) saveTitle(titleInput.trim()); }}
              disabled={titlePending || !titleInput.trim()}
              className="h-[44px] px-[16px] rounded-[32px] bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all disabled:opacity-50"
            >
              {t('common.save')}
            </button>
            <button
              type="button"
              onClick={() => setEditingTitle(false)}
              className="h-[44px] px-[16px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[13px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
            >
              {t('common.cancel')}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-[10px] mb-[6px]">
            <h1 className="font-display text-[24px] md:text-[28px] font-bold text-base-black">
              {review.title}
            </h1>
            {isOwner && (
              <button
                type="button"
                onClick={() => { setTitleInput(review.title); setEditingTitle(true); }}
                className="text-[#818185] hover:text-base-black transition-colors flex-shrink-0"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className="flex items-center gap-[8px] mb-[10px] flex-wrap">
          <MeetingStatusPill
            started={!!(review as any).meetingStarted}
            isAdmin={true}
            onToggle={() => toggleMeeting()}
          />
          {review.teams?.length > 0 && review.teams.map((team) => (
            <span key={team.id} className="group inline-flex items-center gap-[5px] px-[10px] py-[3px] rounded-[5px] bg-[#EFEFF1] text-[#4A4A4C] text-[12px] font-semibold hover:bg-[#E5E5ED] transition-all">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
              <Link to={`/team/${team.id}`} className="hover:underline">{team.name}</Link>
              {isOwner && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeTeam(team.id); }}
                  className="md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-150 text-[#BFBFC0] hover:text-[#D8210C] flex-shrink-0"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </span>
          ))}
          {isOwner && (() => {
            const assignedIds = new Set((review.teams || []).map((t: any) => t.id));
            const available = userTeams && Array.isArray(userTeams)
              ? (userTeams as any[]).filter((t: any) => !assignedIds.has(t.id))
              : [];
            return available.length > 0 ? (
              <AddTeamPopover available={available} onAdd={addTeam} />
            ) : null;
          })()}
          <p className="font-body text-[14px] text-gray-900">
            {review.participants.length} participant{review.participants.length !== 1 ? 's' : ''}
          </p>
          {(review as any).waitEstimate?.avgMinutes && ((review as any).featureToggles?.waitEstimates !== false) && (
            <span className="text-[12px] text-[#818185] font-body">· avg ~{(review as any).waitEstimate.avgMinutes} min/turn</span>
          )}
        </div>
        {/* Event link & password pills — stacked */}
        <div className="flex flex-col gap-[8px] mb-[10px]">
          {/* Event link pill */}
          <div className="flex gap-[8px] flex-wrap items-center">
          {editingUrl ? (
            <div className="flex gap-[6px] flex-1 min-w-[250px]">
              <input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder={t('review.meetingLinkPlaceholder')}
                className="flex-1 h-[36px] px-[12px] text-[13px] font-body bg-white border border-gray-700 rounded-[18px] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
                onKeyDown={(e) => { if (e.key === 'Enter') saveMeetingUrl(urlInput); if (e.key === 'Escape') setEditingUrl(false); }}
              />
              <button type="button" onClick={() => saveMeetingUrl(urlInput)} className="h-[36px] px-[12px] rounded-[18px] bg-[#1D1D1E] text-white font-body text-[12px] font-medium hover:bg-[#2E2E30] transition-all">{t('common.save')}</button>
              <button type="button" onClick={() => setEditingUrl(false)} className="h-[36px] px-[12px] rounded-[18px] border border-gray-500 bg-gray-400 font-body text-[12px] font-medium text-base-black hover:bg-[#E0E0E3] transition-all">{t('common.cancel')}</button>
            </div>
          ) : meetingHref ? (
            <>
              <a
                href={meetingHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-[5px] h-[36px] px-[14px] bg-[#1D1D1E] text-white rounded-[32px] font-body text-[12px] font-medium hover:bg-[#2E2E30] transition-all max-w-[240px]"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <path d="M15 3h6v6" /><path d="M10 14L21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                </svg>
                <span className="truncate">{review.meetingLinkLabel || t('review.joinReview')}</span>
              </a>
              {editingLabel ? (
                <div className="flex gap-[6px] items-center">
                  <input
                    value={labelInput}
                    onChange={(e) => setLabelInput(e.target.value)}
                    placeholder={t('review.joinReview')}
                    maxLength={40}
                    autoFocus
                    className="h-[36px] w-[160px] px-[12px] text-[13px] font-body bg-white border border-gray-700 rounded-[18px] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
                    onKeyDown={(e) => { if (e.key === 'Enter') saveLinkLabel(labelInput); if (e.key === 'Escape') setEditingLabel(false); }}
                  />
                  <button type="button" onClick={() => saveLinkLabel(labelInput)} className="h-[36px] px-[12px] rounded-[18px] bg-[#1D1D1E] text-white font-body text-[12px] font-medium hover:bg-[#2E2E30] transition-all">{t('common.save')}</button>
                  <button type="button" onClick={() => setEditingLabel(false)} className="text-[#818185] hover:text-[#D8210C] transition-colors flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => { setLabelInput(review.meetingLinkLabel || ''); setEditingLabel(true); }}
                    title={t('review.renameEventLink')}
                    className="text-[#818185] hover:text-base-black transition-colors flex-shrink-0"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setUrlInput(review.meetingUrl || ''); setEditingUrl(true); }}
                    title={t('review.editEventUrl')}
                    className="text-[#818185] hover:text-base-black transition-colors flex-shrink-0"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                  </button>
                </>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={() => { setUrlInput(''); setEditingUrl(true); }}
              className="inline-flex items-center gap-[4px] h-[36px] px-[14px] rounded-[32px] bg-[#EFEFF1] dark:bg-[#28282B] text-[#818185] dark:text-[#A0A0A3] font-body text-[12px] font-semibold hover:bg-[#E5E5ED] dark:hover:bg-[#333336] hover:text-[#4A4A4C] dark:hover:text-white transition-all"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              {t('review.addMeetingLink')}
            </button>
          )}
          </div>
          {/* Password pill — hidden for Free users and when company queuePasswords toggle is off */}
          {user?.isPro && ((review as any).featureToggles?.queuePasswords !== false) && (
          <div className="flex gap-[8px] flex-wrap items-center">
          {editingPassword ? (
            <div className="flex gap-[6px] flex-1 min-w-[250px] items-center">
              <input
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder={t('review.passwordPlaceholder')}
                type={showPassword ? 'text' : 'password'}
                className="flex-1 h-[36px] px-[12px] text-[13px] font-body bg-white border border-gray-700 rounded-[18px] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
                onKeyDown={(e) => { if (e.key === 'Enter') savePassword(passwordInput); if (e.key === 'Escape') setEditingPassword(false); }}
              />
              <button type="button" onClick={() => savePassword(passwordInput)} className="h-[36px] px-[12px] rounded-[18px] bg-[#1D1D1E] text-white font-body text-[12px] font-medium hover:bg-[#2E2E30] transition-all">{t('common.save')}</button>
              <button type="button" onClick={() => setEditingPassword(false)} className="h-[36px] px-[12px] rounded-[18px] border border-gray-500 bg-gray-400 font-body text-[12px] font-medium text-base-black hover:bg-[#E0E0E3] transition-all">{t('common.cancel')}</button>
            </div>
          ) : (passwordData as any)?.password ? (
            <>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText((passwordData as any).password);
                  setCopiedPassword(true);
                  setTimeout(() => setCopiedPassword(false), 2000);
                }}
                className="inline-flex items-center gap-[5px] h-[36px] px-[14px] rounded-[32px] bg-[#EFEFF1] dark:bg-[#28282B] text-[#4A4A4C] dark:text-[#A0A0A3] font-body text-[12px] font-semibold hover:bg-[#E5E5ED] dark:hover:bg-[#333336] dark:hover:text-white transition-all"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                {copiedPassword ? <span>{t('common.copied')}</span> : <code className="font-mono text-[11px]">{showPassword ? (passwordData as any).password : '••••••'}</code>}
              </button>
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[#818185] hover:text-base-black transition-colors flex-shrink-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {showPassword ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></> : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>}
                </svg>
              </button>
              <button
                type="button"
                onClick={() => { setPasswordInput((passwordData as any)?.password || ''); setEditingPassword(true); }}
                className="text-[#818185] hover:text-base-black transition-colors flex-shrink-0"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                setPasswordInput('');
                setEditingPassword(true);
              }}
              className="inline-flex items-center gap-[4px] h-[36px] px-[14px] rounded-[32px] bg-[#EFEFF1] dark:bg-[#28282B] text-[#818185] dark:text-[#A0A0A3] font-body text-[12px] font-semibold hover:bg-[#E5E5ED] dark:hover:bg-[#333336] hover:text-[#4A4A4C] dark:hover:text-white transition-all"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              {t('review.setPassword')}
            </button>
          )}
          </div>
          )}
        </div>
        {/* Action buttons row */}
        <div className="flex gap-[8px] flex-wrap mb-[10px]">
          <InviteTracker
            slug={slug!}
            isPro={user?.isPro || false}
            onUpgrade={() => setUpgradeMessage(t('review.inviteTrackerProRequired'))}
          />
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="h-[36px] px-[14px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[13px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all flex items-center gap-[6px]"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
            </svg>
            {t('inviteModal.shareQueue')}
          </button>
          {isOwner && (
            <button
              type="button"
              onClick={() => setShowEndModal(true)}
              className="h-[36px] px-[14px] rounded-[32px] border border-red-500 bg-white font-body text-[13px] font-medium text-red-500 hover:bg-red-500 hover:text-white transition-all"
            >
              {t('review.endReview')}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-[16px] border border-[rgba(0,0,0,0.11)] p-[16px] md:p-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07)]">

      {active.length > 0 && (
        <div className="mb-[16px]">
          <p className="font-body text-[12px] font-semibold text-gray-900 uppercase tracking-wider mb-[6px] ml-[4px]">
            {t('review.inMeeting')}
          </p>
          {active.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-[12px] p-[12px_16px] rounded-[16px] border border-[#1D1D1E] bg-[#F5F5F6] shadow-[0_4px_20px_rgba(0,0,0,0.1)] mb-[6px]"
            >
              <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white text-[12px] font-semibold flex-shrink-0">
                {getInitials(p.name)}
              </span>
              <span className="flex-1 font-body text-[16px] font-semibold text-[#111827]">
                {p.name}
              </span>
              <button
                type="button"
                onClick={() => setStatus({ participantId: p.id, status: 'done' })}
                className="text-[12px] font-medium px-[10px] py-[4px] rounded-[8px] bg-green-500 text-white hover:bg-green-600 transition-all"
              >
                {t('review.markDone')}
              </button>
            </div>
          ))}
        </div>
      )}

      {waiting.length > 0 && (
        <div className="mb-[16px]">
          <p className="font-body text-[12px] font-semibold text-gray-900 uppercase tracking-wider mb-[6px] ml-[4px]">
            {t('review.queue')}
          </p>
          {waiting.map((p, idx) => (
            <React.Fragment key={p.id}>
              <div
                ref={(el) => { if (el) cardRefsMap.current.set(p.id, el); }}
                draggable
                onDragStart={() => handleDragStart(p.id)}
                onDragOver={(e) => handleDragOver(e, p.id)}
                onDrop={(e) => handleDrop(e, p.id, waiting)}
                onDragEnd={handleDragEnd}
                onTouchStart={(e) => handleTouchStart(p.id, e)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={() => { if (touchTimerRef.current) { clearTimeout(touchTimerRef.current); touchTimerRef.current = null; } if (!isTouchDragging.current) { setDragId(null); setDragOverId(null); } }}
                data-draggable="true"
                style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none', touchAction: 'none' } as React.CSSProperties}
                className={`flex items-center gap-[8px] p-[10px_12px] md:p-[10px_16px] rounded-[16px] border bg-white mb-[6px] cursor-grab active:cursor-grabbing select-none transition-all duration-150 overflow-hidden
                  ${
                    dragOverId === p.id && dragId !== p.id
                      ? 'border-[2px] border-[#1D1D1E] bg-[#F0F0F2] shadow-[0_8px_30px_rgba(0,0,0,0.15)]'
                      : idx === 0
                        ? 'border-orange-400 shadow-[0_2px_10px_rgba(251,146,60,0.15)]'
                        : idx === 1
                          ? 'border-yellow-400 shadow-[0_2px_10px_rgba(234,179,8,0.1)]'
                          : 'border-[rgba(0,0,0,0.11)] shadow-[0_1px_3px_rgba(0,0,0,0.07)]'
                  }
                  ${dragId === p.id ? 'opacity-30' : ''}
                `}
              >
                <svg
                  className="flex-shrink-0 text-gray-700"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <circle cx="5" cy="3" r="1.5" />
                  <circle cx="11" cy="3" r="1.5" />
                  <circle cx="5" cy="8" r="1.5" />
                  <circle cx="11" cy="8" r="1.5" />
                  <circle cx="5" cy="13" r="1.5" />
                  <circle cx="11" cy="13" r="1.5" />
                </svg>
                <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white text-[12px] font-semibold flex-shrink-0">
                  {getInitials(p.name)}
                </span>
                <span className="flex-1 font-body text-[15px] font-semibold text-[#111827] truncate">
                  {p.name}
                </span>
                <span className="font-mono text-[12px] text-gray-700 flex-shrink-0">
                  #{idx + 1}
                </span>
                <span className={`text-[10px] font-semibold px-[6px] py-[2px] rounded-[4px] min-w-[55px] md:min-w-[85px] text-center flex-shrink-0 ${
                  idx === 0
                    ? 'bg-orange-400 text-orange-800'
                    : idx === 1
                      ? 'bg-yellow-400 text-yellow-700'
                      : 'bg-gray-300 text-gray-600'
                }`}>
                  {idx === 0 ? t('review.nextUp') : idx === 1 ? t('review.standby') : t('review.waiting')}
                </span>
                <div className="flex items-center gap-[6px] flex-shrink-0">
                  <button
                    type="button"
                    onTouchStart={(e) => e.stopPropagation()}
                    onClick={() =>
                      setStatus({
                        participantId: p.id,
                        status: 'active'
                      })
                    }
                    className="text-[11px] font-medium px-[8px] py-[3px] rounded-[8px] bg-[#1D1D1E] text-white hover:bg-[#2E2E30] transition-all"
                  >
                    {t('review.activate')}
                  </button>
                  <button
                    type="button"
                    onTouchStart={(e) => e.stopPropagation()}
                    onClick={() => remove(p.id)}
                    className="text-[11px] font-medium px-[8px] py-[3px] rounded-[8px] border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all"
                  >
                    {t('common.remove')}
                  </button>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {done.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-[6px] ml-[4px] flex-wrap gap-[6px]">
            <p className="font-body text-[12px] font-semibold text-gray-900 dark:text-[#6B6B6F] uppercase tracking-wider">
              {t('review.completed')}
            </p>
            {user?.isPro && (
            <div className="flex items-center gap-[6px]">
              <button
                type="button"
                onClick={() => setShowReinstateAllModal(true)}
                className="inline-flex items-center gap-[4px] h-[28px] px-[10px] rounded-[32px] border border-[#0ea5c9]/30 dark:border-[#0ea5c9]/20 bg-[#F8FCFF] dark:bg-[#0ea5c9]/10 font-body text-[11px] font-medium text-[#0ea5c9] hover:bg-[#EDF9FF] dark:hover:bg-[#0ea5c9]/15 transition-all"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>
                {t('review.reinstateAll')}
              </button>
              <button
                type="button"
                onClick={() => setShowClearAllModal(true)}
                className="inline-flex items-center gap-[4px] h-[28px] px-[10px] rounded-[32px] border border-[#D8210C]/30 dark:border-[#D8210C]/20 bg-[#FEF2F2] dark:bg-[#D8210C]/10 font-body text-[11px] font-medium text-[#D8210C] hover:bg-[#FEE2E2] dark:hover:bg-[#D8210C]/15 transition-all"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                {t('review.clearAll')}
              </button>
            </div>
            )}
          </div>
          {done.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-[12px] p-[10px_16px] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] bg-white dark:bg-[#1C1C1F] opacity-60 hover:opacity-80 mb-[6px] transition-all group"
            >
              <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-gray-700 dark:bg-[#4A4A4D] text-white text-[12px] font-semibold flex-shrink-0">
                {getInitials(p.name)}
              </span>
              <span className="flex-1 font-body text-[15px] font-medium text-gray-900 dark:text-[#A0A0A3] truncate">
                {p.name}
              </span>
              <span className="flex-shrink-0 text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] min-w-[85px] text-center bg-gray-400 text-gray-700 dark:bg-[#333336] dark:text-[#6B6B6F]">{t('review.done')}</span>
              {user?.isPro && (
              <div className="flex items-center gap-[4px] flex-shrink-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-150">
                <button
                  type="button"
                  onClick={() => setConfirmReinstate(p)}
                  className="flex items-center justify-center w-[28px] h-[28px] rounded-[8px] border border-transparent text-[#0ea5c9] hover:bg-[#F8FCFF] hover:border-[#0ea5c9]/30 dark:hover:bg-[#0ea5c9]/10 dark:hover:border-[#0ea5c9]/30 transition-all"
                  title={t('review.reinstate')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="1 4 1 10 7 10" />
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRemove(p)}
                  className="flex items-center justify-center w-[28px] h-[28px] rounded-[8px] border border-transparent text-[#D8210C] hover:bg-[#FEF2F2] hover:border-[#D8210C]/30 dark:hover:bg-[#D8210C]/10 dark:hover:border-[#D8210C]/30 transition-all"
                  title={t('common.remove')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              )}
            </div>
          ))}
        </div>
      )}

      {review.participants.length === 0 && (
        <div className="text-center py-[40px]">
          <p className="font-body text-[16px] text-gray-900 mb-[8px]">
            {t('review.noParticipants')}
          </p>
          <p className="font-body text-[14px] text-gray-700">
            {t('review.shareLink')}
          </p>
        </div>
      )}

      </div>

      {isOwner && slug && (
        <QueueSettingsModal
          slug={slug}
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          hasTeam={review.teams?.length > 0}
          isPro={user?.isPro || false}
          isCompany={!!review.companyId}
          featureToggles={(review as any).featureToggles}
        />
      )}

      {slug && ((review as any).featureToggles?.chat !== false) && (
        <>
          <ChatToggleButton onClick={() => setChatOpen(true)} unreadCount={unreadCount} />
          <ChatSidebar
            slug={slug}
            senderName={
              user?.email ? `Admin (${user.displayName || user.email.split('@')[0]})` : 'Admin'
            }
            participants={review.participants}
            isTeamReview={review.teams?.length > 0}
            isAdmin={true}
            adminNames={review.adminNames || [adminName]}
            isOpen={chatOpen}
            onClose={() => setChatOpen(false)}
          />
        </>
      )}

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title={t('inviteModal.shareQueue')}
        copyLabel={t('inviteModal.queueLink')}
        copyUrl={`${window.location.origin}/review/${slug}`}
        isPro={user?.isPro || false}
        onUpgrade={() => setUpgradeMessage(t('review.inviteTrackerProRequired'))}
        onSendInvites={async (emails) => {
          const participants = (review as IReviewPoll)?.participants ?? [];
          if (!user?.isPro && participants.length >= 15) {
            setInviteOpen(false);
            setUpgradeMessage(t('review.participantLimitReached'));
            return;
          }
          await reviewService.sendInvites(slug!, emails);
          const remaining = !user?.isPro ? Math.max(0, 15 - participants.length) : null;
          if (remaining !== null && emails.length > remaining) {
            toast.success(`Invited ${emails.length} ${emails.length === 1 ? 'person' : 'people'}. Note: only ${remaining} more can join on the free plan`);
          } else {
            toast.success(`Invited ${emails.length} ${emails.length === 1 ? 'person' : 'people'}`);
          }
        }}
      />

      {upgradeMessage && (
        <UpgradeModal message={upgradeMessage} onClose={() => setUpgradeMessage(null)} />
      )}

      {showEndModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowEndModal(false); }}
        >
          <div className="bg-white rounded-[16px] border border-[rgba(0,0,0,0.11)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[380px] p-[24px] flex flex-col gap-[16px] text-center">
            <div>
              <h2 className="font-display text-[20px] font-bold text-base-black mb-[4px]">
                {t('review.endReview')}
              </h2>
              <p className="font-body text-[14px] text-gray-900">
                {t('review.endReviewConfirmMessage')}
              </p>
            </div>
            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={() => { endReview(); setShowEndModal(false); }}
                className="flex-1 h-[44px] rounded-[32px] bg-[#D8210C] text-white font-body text-[14px] font-medium hover:bg-[#b91c0a] transition-all"
              >
                {t('review.endReviewConfirm')}
              </button>
              <button
                type="button"
                onClick={() => setShowEndModal(false)}
                className="flex-1 h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showClearAllModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowClearAllModal(false); }}
        >
          <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[380px] p-[24px] flex flex-col gap-[16px] text-center">
            <div>
              <h2 className="font-display text-[20px] font-bold text-base-black dark:text-[#E8E8EA] mb-[4px]">
                {t('review.clearAllConfirmTitle')}
              </h2>
              <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3]">
                {t('review.clearAllConfirmMessage', { count: done.length })}
              </p>
            </div>
            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={() => clearCompleted()}
                className="flex-1 h-[44px] rounded-[32px] bg-[#D8210C] text-white font-body text-[14px] font-medium hover:bg-[#b91c0a] transition-all"
              >
                {t('review.clearAllConfirm')}
              </button>
              <button
                type="button"
                onClick={() => setShowClearAllModal(false)}
                className="flex-1 h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReinstateAllModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowReinstateAllModal(false); }}
        >
          <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[380px] p-[24px] flex flex-col gap-[16px] text-center">
            <div>
              <h2 className="font-display text-[20px] font-bold text-base-black dark:text-[#E8E8EA] mb-[4px]">
                {t('review.reinstateAllConfirmTitle')}
              </h2>
              <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3]">
                {t('review.reinstateAllConfirmMessage', { count: done.length })}
              </p>
            </div>
            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={() => reinstateAll()}
                className="flex-1 h-[44px] rounded-[32px] bg-[#0ea5c9] text-white font-body text-[14px] font-medium hover:bg-[#0991b3] transition-all"
              >
                {t('review.reinstateAllConfirm')}
              </button>
              <button
                type="button"
                onClick={() => setShowReinstateAllModal(false)}
                className="flex-1 h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmReinstate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmReinstate(null); }}
        >
          <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[380px] p-[24px] flex flex-col gap-[16px] text-center">
            <div>
              <h2 className="font-display text-[20px] font-bold text-base-black dark:text-[#E8E8EA] mb-[4px]">
                {t('review.reinstateConfirmTitle')}
              </h2>
              <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3]">
                {t('review.reinstateConfirmMessage', { name: confirmReinstate.name })}
              </p>
            </div>
            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={() => { setStatus({ participantId: confirmReinstate.id, status: 'waiting' }); setConfirmReinstate(null); }}
                className="flex-1 h-[44px] rounded-[32px] bg-[#0ea5c9] text-white font-body text-[14px] font-medium hover:bg-[#0991b3] transition-all"
              >
                {t('review.reinstateConfirm')}
              </button>
              <button
                type="button"
                onClick={() => setConfirmReinstate(null)}
                className="flex-1 h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmRemove && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmRemove(null); }}
        >
          <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[380px] p-[24px] flex flex-col gap-[16px] text-center">
            <div>
              <h2 className="font-display text-[20px] font-bold text-base-black dark:text-[#E8E8EA] mb-[4px]">
                {t('review.removeConfirmTitle')}
              </h2>
              <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3]">
                {t('review.removeConfirmMessage', { name: confirmRemove.name })}
              </p>
            </div>
            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={() => { remove(confirmRemove.id); setConfirmRemove(null); }}
                className="flex-1 h-[44px] rounded-[32px] bg-[#D8210C] text-white font-body text-[14px] font-medium hover:bg-[#b91c0a] transition-all"
              >
                {t('review.removeConfirm')}
              </button>
              <button
                type="button"
                onClick={() => setConfirmRemove(null)}
                className="flex-1 h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Admin Management Panel ---

const AdminPanel = ({ slug, hasTeam, isPro }: { slug: string; hasTeam: boolean; isPro: boolean }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [email, setEmail] = React.useState('');
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [coAdminModal, setCoAdminModal] = React.useState<{
    email: string;
    exists: boolean;
    alreadyAdmin: boolean;
    isOwner: boolean;
    alreadyInvited: boolean;
  } | null>(null);
  const [checkPending, setCheckPending] = React.useState(false);
  const [revokeTarget, setRevokeTarget] = React.useState<{ id: number; email: string } | null>(null);
  const [resendingId, setResendingId] = React.useState<number | null>(null);

  const { data: adminData } = useQuery({
    queryKey: ['review-admins', slug],
    queryFn: () => reviewService.getReviewAdmins(slug),
    staleTime: 0
  });

  const { data: pendingInvites } = useQuery({
    queryKey: ['review-pending-admins', slug],
    queryFn: () => reviewService.getPendingCoAdminInvites(slug),
    staleTime: 0,
    enabled: isPro
  });

  const { data: teamMembers } = useQuery({
    queryKey: ['review-team-members', slug],
    queryFn: () => reviewService.getTeamMembersForReview(slug),
    enabled: hasTeam
  });

  const { mutate: removeAdmin } = useMutation({
    mutationFn: (id: number) => reviewService.removeCoAdmin(slug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-admins', slug] });
      toast.success(t('review.coAdminRemoved'));
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setCheckPending(true);
    try {
      const result = await reviewService.checkCoAdminEmail(slug, trimmed);
      if (result.alreadyAdmin) {
        toast.error(t('review.coAdminAlreadyAdmin'));
      } else if (result.isOwner) {
        toast.error(t('review.coAdminIsOwner'));
      } else if (result.alreadyInvited) {
        toast.error(t('review.coAdminAlreadyInvited'));
      } else {
        setCoAdminModal(result);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message;
      toast.error(msg || t('review.coAdminAddFailed'));
    } finally {
      setCheckPending(false);
    }
  };

  const handleConfirmAdd = async () => {
    if (!coAdminModal) return;
    try {
      if (coAdminModal.exists) {
        await reviewService.addCoAdmin(slug, coAdminModal.email);
        toast.success(t('review.coAdminAdded'));
      } else {
        await reviewService.inviteCoAdmin(slug, coAdminModal.email);
        toast.success(t('review.coAdminInviteSent'));
      }
      setEmail('');
      setCoAdminModal(null);
      queryClient.invalidateQueries({ queryKey: ['review-admins', slug] });
      queryClient.invalidateQueries({ queryKey: ['review-pending-admins', slug] });
    } catch (err: any) {
      const msg = err.response?.data?.message;
      toast.error(msg || t('review.coAdminAddFailed'));
    }
  };

  const handleResendInvite = async (inviteId: number) => {
    setResendingId(inviteId);
    try {
      await reviewService.resendCoAdminInvite(slug, inviteId);
      toast.success(t('review.coAdminInviteResent'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('review.coAdminAddFailed'));
    } finally {
      setResendingId(null);
    }
  };

  const handleRevokeInvite = async () => {
    if (!revokeTarget) return;
    try {
      await reviewService.revokeCoAdminInvite(slug, revokeTarget.id);
      toast.success(t('review.coAdminInviteRevoked'));
      setRevokeTarget(null);
      queryClient.invalidateQueries({ queryKey: ['review-pending-admins', slug] });
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('review.coAdminAddFailed'));
    }
  };

  const coAdmins = (adminData as any)?.coAdmins || [];

  return (
    <div>
      <h2 className="font-body text-[14px] font-semibold text-base-black dark:text-white mb-[12px]">
        {t('review.reviewAdmins')}
      </h2>

      <div className="flex flex-col gap-[8px] mb-[16px]">
        {/* Owner */}
        <div className="flex items-center gap-[12px] p-[10px_14px] rounded-[12px] bg-[#F5F5F6] border border-[rgba(0,0,0,0.07)]">
          <span className="w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white flex items-center justify-center text-[11px] font-bold uppercase flex-shrink-0">
            OW
          </span>
          <span className="flex-1 font-body text-[14px] text-base-black truncate">
            {t('review.youOwner')}
          </span>
          <span className="text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] bg-[#1D1D1E] text-white">
            {t('review.owner')}
          </span>
        </div>

        {/* Co-admins */}
        {coAdmins.map((admin: any) => (
          <div
            key={admin.id}
            className="flex items-center gap-[12px] p-[10px_14px] rounded-[12px] bg-white border border-[rgba(0,0,0,0.11)]"
          >
            <span className="w-[32px] h-[32px] rounded-full bg-[#4A4A4C] text-white flex items-center justify-center text-[11px] font-bold uppercase flex-shrink-0">
              {admin.email.slice(0, 2).toUpperCase()}
            </span>
            <span className="flex-1 min-w-0 font-body text-[14px] text-base-black truncate">
              {admin.email}
            </span>
            <span className="text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] bg-[#EFEFF1] text-[#4A4A4C]">
              {t('header.admin')}
            </span>
            <button
              type="button"
              onClick={() => removeAdmin(admin.id)}
              className="text-[11px] font-medium px-[8px] py-[3px] rounded-[8px] border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all flex-shrink-0"
            >
              {t('common.remove')}
            </button>
          </div>
        ))}

        {/* Pending co-admin invites */}
        {Array.isArray(pendingInvites) && pendingInvites.map((invite: any) => (
          <div
            key={`pending-${invite.id}`}
            className="flex flex-wrap items-center gap-[8px] tablet:gap-[12px] p-[10px_14px] rounded-[12px] bg-white border border-dashed border-[rgba(0,0,0,0.18)]"
          >
            <span className="w-[32px] h-[32px] rounded-full bg-[#BFBFC0] text-white flex items-center justify-center text-[11px] font-bold uppercase flex-shrink-0">
              {invite.email.slice(0, 2).toUpperCase()}
            </span>
            <span className="flex-1 min-w-0 font-body text-[14px] text-[#818185] truncate">
              {invite.email}
            </span>
            <span className="text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] bg-[#FFF8E1] text-[#B8860B] flex-shrink-0">
              {t('review.coAdminPending')}
            </span>
            <div className="flex gap-[6px] w-full tablet:w-auto">
              <button
                type="button"
                onClick={() => handleResendInvite(invite.id)}
                disabled={resendingId === invite.id}
                className="flex-1 tablet:flex-initial text-[11px] font-medium px-[8px] py-[3px] rounded-[8px] border border-[#0CB3D8] text-[#0CB3D8] hover:bg-[#0CB3D8] hover:text-white transition-all disabled:opacity-50"
              >
                {resendingId === invite.id ? t('review.coAdminResending') : t('review.coAdminResend')}
              </button>
              <button
                type="button"
                onClick={() => setRevokeTarget({ id: invite.id, email: invite.email })}
                className="flex-1 tablet:flex-initial text-[11px] font-medium px-[8px] py-[3px] rounded-[8px] border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all"
              >
                {t('review.coAdminRevoke')}
              </button>
            </div>
          </div>
        ))}

        {coAdmins.length === 0 && (!Array.isArray(pendingInvites) || pendingInvites.length === 0) && (
          <p className="font-body text-[13px] text-gray-900 ml-[4px]">
            {t('review.noCoAdmins')}
          </p>
        )}
      </div>

      {/* Add co-admin form */}
      {!isPro ? (
        <div className="flex gap-[8px] items-center">
          <input
            disabled
            placeholder={t('review.enterEmail')}
            className="flex-1 h-[40px] px-[14px] text-[14px] font-body bg-white border border-gray-700 rounded-[24px] placeholder-gray-900 opacity-40 cursor-not-allowed"
          />
          <button
            type="button"
            disabled
            className="h-[40px] px-[16px] rounded-[32px] font-body text-[13px] font-medium text-white bg-gray-900 opacity-40 cursor-not-allowed flex-shrink-0"
          >
            {t('review.addAdmin')}
          </button>
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="relative flex gap-[8px] items-center">
        <div className="relative flex-1">
          <input
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setShowSuggestions(e.target.value.length >= 2 && hasTeam);
            }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder={hasTeam ? 'Type to search team members...' : 'Enter email to add as admin'}
            type={hasTeam ? 'text' : 'email'}
            autoComplete="off"
            className="w-full h-[40px] px-[14px] text-[14px] font-body text-base-black bg-white border border-gray-700 rounded-[24px] placeholder-gray-900 focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
          />
          {showSuggestions && Array.isArray(teamMembers) && (() => {
            const existing = coAdmins.map((a: any) => a.email);
            const filtered = (teamMembers as any[]).filter(
              (m: any) => !existing.includes(m.email) && (
                m.email.toLowerCase().includes(email.toLowerCase()) ||
                (m.displayName && m.displayName.toLowerCase().includes(email.toLowerCase()))
              )
            );
            return filtered.length > 0 ? (
              <div className="absolute top-full left-0 right-0 mt-[4px] bg-white rounded-[12px] border border-[rgba(0,0,0,0.11)] shadow-[0_4px_20px_rgba(0,0,0,0.1)] py-[4px] max-h-[160px] overflow-y-auto z-10">
                {filtered.map((m: any) => (
                  <button
                    key={m.email}
                    type="button"
                    onMouseDown={() => {
                      setEmail(m.email);
                      setShowSuggestions(false);
                    }}
                    className="w-full text-left px-[12px] py-[8px] font-body text-[14px] text-base-black flex items-center gap-[8px] hover:bg-[#F0F0F2] transition-colors"
                  >
                    <span className="w-[24px] h-[24px] rounded-full bg-[#1D1D1E] text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                      {m.email.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="truncate">
                      {m.displayName ? `${m.displayName} (${m.email})` : m.email}
                    </span>
                  </button>
                ))}
              </div>
            ) : null;
          })()}
        </div>
        <button
          type="submit"
          disabled={checkPending || !email.trim()}
          className={`h-[40px] px-[16px] rounded-[32px] font-body text-[13px] font-medium text-white transition-all flex-shrink-0 ${
            checkPending || !email.trim()
              ? 'bg-gray-900 opacity-50'
              : 'bg-base-black hover:bg-[#2E2E30]'
          }`}
        >
          {checkPending ? t('review.adding') : t('review.addAdmin')}
        </button>
      </form>
      )}
      {!isPro && (
        <div className="mt-[12px]">
          <span className="inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-[5px] bg-[#EFEFF1] text-[#818185] text-[12px] font-semibold">
            {t('review.webhookProFeature')}
          </span>
        </div>
      )}

      {revokeTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setRevokeTarget(null); }}
        >
          <div className="bg-white rounded-[16px] border border-[rgba(0,0,0,0.11)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[380px] p-[24px] flex flex-col gap-[16px] text-center">
            <div>
              <h2 className="font-display text-[20px] font-bold text-base-black mb-[4px]">
                {t('review.coAdminRevokeTitle')}
              </h2>
              <p className="font-body text-[14px] text-gray-900">
                {t('review.coAdminRevokeMessage', { email: revokeTarget.email })}
              </p>
            </div>
            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={handleRevokeInvite}
                className="flex-1 h-[44px] rounded-[32px] bg-[#D8210C] text-white font-body text-[14px] font-medium hover:bg-[#b91c0a] transition-all"
              >
                {t('review.coAdminRevokeConfirm')}
              </button>
              <button
                type="button"
                onClick={() => setRevokeTarget(null)}
                className="flex-1 h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {coAdminModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setCoAdminModal(null); }}
        >
          <div className="bg-white rounded-[16px] border border-[rgba(0,0,0,0.11)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[420px] p-[24px] flex flex-col gap-[16px]">
            <h2 className="font-display text-[20px] font-bold text-base-black">
              {coAdminModal.exists ? t('review.coAdminConfirmTitle') : t('review.coAdminInviteTitle')}
            </h2>

            <div className="flex items-center gap-[12px] p-[12px_14px] rounded-[12px] bg-[#F5F5F6] border border-[rgba(0,0,0,0.07)]">
              <span className="w-[36px] h-[36px] rounded-full bg-[#4A4A4C] text-white flex items-center justify-center text-[12px] font-bold uppercase flex-shrink-0">
                {coAdminModal.email.slice(0, 2).toUpperCase()}
              </span>
              <span className="font-body text-[14px] text-base-black truncate">{coAdminModal.email}</span>
              {coAdminModal.exists && (
                <span className="ml-auto text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] bg-[#DFF7FF] text-[#0CB3D8] flex-shrink-0">
                  {t('review.coAdminRegistered')}
                </span>
              )}
            </div>

            <p className="font-body text-[14px] text-gray-900 leading-[1.5]">
              {coAdminModal.exists
                ? t('review.coAdminConfirmMessage')
                : t('review.coAdminInviteMessage')}
            </p>

            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={handleConfirmAdd}
                className="flex-1 h-[44px] rounded-[32px] bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[14px] font-medium hover:bg-[#29292A] dark:hover:bg-[#D8D8DB] transition-all"
              >
                {coAdminModal.exists ? t('review.coAdminConfirmAdd') : t('review.coAdminConfirmInvite')}
              </button>
              <button
                type="button"
                onClick={() => setCoAdminModal(null)}
                className="flex-1 h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Add Team Popover (inline in header) ---

const AddTeamPopover = ({ available, onAdd }: { available: any[]; onAdd: (id: number) => void }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-[4px] px-[10px] py-[3px] rounded-[5px] bg-[#EFEFF1] text-[#818185] text-[12px] font-semibold hover:bg-[#E5E5ED] hover:text-[#4A4A4C] transition-all"
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        {t('review.addTeam')}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-[8px] min-w-[200px] bg-white rounded-[12px] border border-[rgba(0,0,0,0.11)] shadow-[0_4px_20px_rgba(0,0,0,0.12)] py-[6px] z-20 animate-fadeInMenu">
          {available.map((team: any) => (
            <button
              key={team.id}
              type="button"
              onClick={() => { onAdd(team.id); setOpen(false); }}
              className="w-full text-left px-[14px] py-[8px] font-body text-[13px] text-[#111827] hover:bg-[#F5F5F6] transition-colors"
            >
              {team.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Webhook Notifications Section (Discord + Slack) ---

const DiscordProviderIcon = ({ size = 20, color = '#5865F2' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z" />
  </svg>
);

const SlackProviderIcon = ({ size = 20, color = '#E01E5A' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </svg>
);

const PROVIDERS = {
  discord: {
    key: 'discord' as const,
    label: 'Discord',
    color: '#5865F2',
    hoverColor: '#4752C4',
    placeholder: 'https://discord.com/api/webhooks/...',
    mask: (url: string) => url.replace(/https:\/\/discord\.com\/api\/webhooks\//, '••••/'),
    Icon: DiscordProviderIcon,
  },
  slack: {
    key: 'slack' as const,
    label: 'Slack',
    color: '#E01E5A',
    hoverColor: '#C01848',
    placeholder: 'https://hooks.slack.com/services/...',
    mask: (url: string) => url.replace(/https:\/\/hooks\.slack\.com\/services\//, '••••/'),
    Icon: SlackProviderIcon,
  },
};

type ProviderKey = 'discord' | 'slack';

const ProviderWebhookPanel = ({
  slug,
  provider,
}: {
  slug: string;
  provider: typeof PROVIDERS[ProviderKey];
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = React.useState(false);
  const [urlInput, setUrlInput] = React.useState('');
  const [channelUrlInput, setChannelUrlInput] = React.useState('');
  const [urlInputFocused, setUrlInputFocused] = React.useState(false);
  const [showSavePrompt, setShowSavePrompt] = React.useState(false);
  const [savePromptName, setSavePromptName] = React.useState('');
  const [showUpdatePrompt, setShowUpdatePrompt] = React.useState(false);
  const [updatePromptWebhookId, setUpdatePromptWebhookId] = React.useState<number | null>(null);
  const [pendingChannelUrl, setPendingChannelUrl] = React.useState('');

  const isSlack = provider.key === 'slack';

  const getFn = isSlack
    ? () => reviewService.getSlackWebhook(slug)
    : () => reviewService.getDiscordWebhook(slug);

  const queryKey = [`${provider.key}-webhook`, slug];

  const { data: webhookData, isLoading: webhookLoading } = useQuery({
    queryKey,
    queryFn: getFn,
    staleTime: 30_000,
  });

  const { data: savedWebhooks = [] } = useQuery<IUserWebhook[]>({
    queryKey: ['user-webhooks'],
    queryFn: () => authService.getUserWebhooks(),
  });

  const suggestions = (savedWebhooks as IUserWebhook[]).filter(
    (wh) => wh.provider === provider.key &&
      (urlInput === '' || wh.name.toLowerCase().includes(urlInput.toLowerCase()) || wh.url.toLowerCase().includes(urlInput.toLowerCase()))
  ).slice(0, 5);

  const { mutate: saveWebhook, isPending } = useMutation({
    mutationFn: ({ url, channelUrl }: { url: string | null; channelUrl?: string | null }) => {
      if (isSlack) return reviewService.setSlackWebhook(slug, url, channelUrl);
      return reviewService.setDiscordWebhook(slug, url);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey });
      setEditing(false);
      toast.success(t('review.webhookSaved', { provider: provider.label }));
      const url = variables.url;
      if (!url) return;
      const savedMatch = (savedWebhooks as IUserWebhook[]).find((wh) => wh.url === url);
      if (!savedMatch) {
        const dismissKey = `standby_webhook_save_dismissed_${btoa(url).slice(0, 16)}`;
        if (!localStorage.getItem(dismissKey)) {
          setShowSavePrompt(true);
        }
      } else if (isSlack && variables.channelUrl && savedMatch.channelUrl !== variables.channelUrl) {
        setPendingChannelUrl(variables.channelUrl);
        setUpdatePromptWebhookId(savedMatch.id);
        setShowUpdatePrompt(true);
      }
    },
    onError: () => toast.error(t('review.webhookSaveFailed')),
  });

  const { mutate: updateSavedWebhook } = useMutation({
    mutationFn: ({ id, channelUrl }: { id: number; channelUrl: string | null }) =>
      authService.updateUserWebhook(id, { channelUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-webhooks'] });
      setShowUpdatePrompt(false);
      toast.success(t('settings.webhookUpdated'));
    },
  });

  const webhookUrl = (webhookData as any)?.webhookUrl || null;
  const savedChannelUrl = (webhookData as any)?.channelUrl || null;
  const isConnected = !!webhookUrl;

  return (
    <div>
      <div className="flex items-center gap-[8px] mb-[4px]">
        <provider.Icon size={18} />
        <h3 className="font-body text-[15px] font-semibold text-base-black">{t('review.webhookProviderTitle', { provider: provider.label })}</h3>
        {isConnected && (
          <span className="inline-flex items-center gap-[4px] px-[8px] py-[2px] rounded-[5px] bg-[#dcfce7] text-[#15803d] text-[11px] font-semibold">
            <span className="w-[6px] h-[6px] rounded-full bg-[#15803d]" />
            {t('review.webhookConnected')}
          </span>
        )}
      </div>
      <p className="font-body text-[13px] text-[#818185] mb-[12px]">
        {t('review.webhookChannelDescription', { provider: provider.label })}
      </p>

      {!editing && !isConnected && !webhookLoading && (
        <button
          type="button"
          onClick={() => { setUrlInput(''); setChannelUrlInput(''); setEditing(true); }}
          style={{ backgroundColor: provider.color }}
          className="h-[36px] px-[14px] rounded-[32px] text-white font-body text-[13px] font-medium transition-all opacity-100 hover:opacity-90"
        >
          {t('review.webhookConnect', { provider: provider.label })}
        </button>
      )}

      {!editing && isConnected && (
        <div className="flex items-center gap-[8px] flex-wrap">
          <span className="font-mono text-[12px] text-[#818185] truncate max-w-[200px]">
            {provider.mask(webhookUrl)}
          </span>
          {isSlack && savedChannelUrl && (
            <a
              href={savedChannelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-[12px] text-[#0ea5c9] hover:underline truncate max-w-[160px]"
            >
              {t('review.openChannel')}
            </a>
          )}
          <button
            type="button"
            onClick={() => { setUrlInput(webhookUrl); setChannelUrlInput(savedChannelUrl || ''); setEditing(true); }}
            className="text-[#818185] hover:text-base-black transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => saveWebhook({ url: null })}
            className="text-[#D8210C] hover:text-red-700 transition-colors text-[12px] font-medium"
          >
            {t('review.webhookDisconnect')}
          </button>
        </div>
      )}

      {editing && (
        <div className="flex flex-col gap-[6px]">
          <div className="flex items-start gap-[6px] flex-wrap">
            <div className="relative flex-1 min-w-0">
              <input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onFocus={() => setUrlInputFocused(true)}
                onBlur={() => setTimeout(() => setUrlInputFocused(false), 150)}
                placeholder={provider.placeholder}
                className="w-full h-[36px] px-[12px] text-[13px] font-body bg-white border border-gray-700 rounded-[18px] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
              />
              {urlInputFocused && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-[4px] bg-white rounded-[12px] border border-[rgba(0,0,0,0.11)] shadow-[0_4px_20px_rgba(0,0,0,0.12)] py-[4px] z-20">
                  {suggestions.map((wh) => (
                    <button
                      key={wh.id}
                      type="button"
                      onClick={() => { setUrlInput(wh.url); if (isSlack) setChannelUrlInput(wh.channelUrl || ''); }}
                      className="w-full text-left px-[12px] py-[8px] font-body text-[13px] text-base-black hover:bg-[#F5F5F6] transition-colors flex items-center gap-[8px]"
                    >
                      <provider.Icon size={14} color={provider.color} />
                      <span className="font-semibold">{wh.name}</span>
                      <span className="text-gray-900 text-[11px] font-mono ml-auto">{wh.provider === 'discord' ? 'discord.com/\u2022\u2022\u2022\u2022' : 'hooks.slack.com/\u2022\u2022\u2022\u2022'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => urlInput.trim() && saveWebhook({ url: urlInput.trim(), channelUrl: channelUrlInput.trim() || null })}
              disabled={isPending || !urlInput.trim()}
              style={{ backgroundColor: provider.color }}
              className="h-[36px] px-[12px] rounded-[18px] text-white font-body text-[12px] font-medium disabled:opacity-50 transition-all opacity-100 hover:opacity-90"
            >
              {isPending ? t('review.webhookSaving') : t('common.save')}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="h-[36px] px-[12px] rounded-[18px] border border-gray-500 bg-gray-400 font-body text-[12px] font-medium text-base-black hover:bg-[#E0E0E3] transition-all"
            >
              {t('common.cancel')}
            </button>
          </div>
          {isSlack && (
            <input
              value={channelUrlInput}
              onChange={(e) => setChannelUrlInput(e.target.value)}
              placeholder={t('review.slackChannelUrlPlaceholder')}
              className="h-[36px] px-[12px] text-[13px] font-body bg-white border border-gray-700 rounded-[18px] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
            />
          )}
        </div>
      )}

      {!editing && isConnected && showSavePrompt && (
        <div className="mt-[10px] p-[12px_14px] rounded-[9px] bg-[#F5F5F6] border border-[rgba(0,0,0,0.08)]">
          <p className="font-body text-[13px] text-base-black font-semibold mb-[8px]">{t('review.webhookSavePrompt')}</p>
          <div className="flex items-center gap-[6px] flex-wrap">
            <input
              value={savePromptName}
              onChange={(e) => setSavePromptName(e.target.value)}
              placeholder={t('settings.webhookNamePlaceholder')}
              className="flex-1 min-w-[140px] h-[32px] px-[10px] text-[13px] font-body bg-white border border-gray-700 rounded-[18px] placeholder-gray-900 focus:outline-none focus:border-[#BFBFC0] transition-all"
            />
            <button
              type="button"
              disabled={!savePromptName.trim()}
              onClick={() => {
                if (!savePromptName.trim() || !webhookUrl) return;
                authService.createUserWebhook({ name: savePromptName, url: webhookUrl, provider: provider.key, channelUrl: (isSlack && channelUrlInput.trim()) ? channelUrlInput.trim() : undefined })
                  .then(() => {
                    queryClient.invalidateQueries({ queryKey: ['user-webhooks'] });
                    setShowSavePrompt(false);
                    setSavePromptName('');
                    toast.success(t('settings.webhookSaved'));
                  })
                  .catch(() => toast.error(t('settings.webhookSaveFailed')));
              }}
              className="h-[32px] px-[10px] rounded-[18px] bg-base-black text-white font-body text-[12px] font-medium disabled:opacity-50 transition-all hover:bg-[#2E2E30]"
            >
              {t('common.save')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (webhookUrl) {
                  const dismissKey = `standby_webhook_save_dismissed_${btoa(webhookUrl).slice(0, 16)}`;
                  localStorage.setItem(dismissKey, '1');
                }
                setShowSavePrompt(false);
              }}
              className="font-body text-[12px] text-gray-900 hover:text-base-black transition-colors"
            >
              {t('common.dismiss')}
            </button>
          </div>
          <p className="font-body text-[11px] text-gray-900 mt-[6px]">
            <button
              type="button"
              onClick={() => navigate('/settings?section=webhooks')}
              className="underline hover:text-base-black transition-colors"
            >
              {t('review.manageWebhooks')}
            </button>
          </p>
        </div>
      )}

      {!editing && isConnected && showUpdatePrompt && updatePromptWebhookId && (
        <div className="mt-[10px] p-[12px_14px] rounded-[9px] bg-[#F5F5F6] border border-[rgba(0,0,0,0.08)]">
          <p className="font-body text-[13px] text-base-black font-semibold mb-[8px]">{t('review.updateSavedWebhookPrompt')}</p>
          <div className="flex items-center gap-[6px]">
            <button
              type="button"
              onClick={() => updateSavedWebhook({ id: updatePromptWebhookId, channelUrl: pendingChannelUrl })}
              className="h-[30px] px-[10px] rounded-[18px] bg-base-black text-white font-body text-[12px] font-medium hover:bg-[#2E2E30] transition-all"
            >
              {t('common.save')}
            </button>
            <button
              type="button"
              onClick={() => setShowUpdatePrompt(false)}
              className="font-body text-[12px] text-gray-900 hover:text-base-black transition-colors"
            >
              {t('common.dismiss')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const WebhookNotificationsSection = ({ slug, isPro }: { slug: string; isPro: boolean }) => {
  const { t } = useTranslation();
  const [activeProvider, setActiveProvider] = React.useState<ProviderKey>('discord');
  const provider = PROVIDERS[activeProvider];

  // Prefetch both providers so tab switch never flashes a loading state
  useQuery({ queryKey: ['discord-webhook', slug], queryFn: () => reviewService.getDiscordWebhook(slug), staleTime: 30_000, enabled: isPro });
  useQuery({ queryKey: ['slack-webhook', slug], queryFn: () => reviewService.getSlackWebhook(slug), staleTime: 30_000, enabled: isPro });

  if (!isPro) {
    return (
      <div>
        <div className="flex items-center gap-[10px] mb-[8px]">
          <DiscordProviderIcon size={18} />
          <SlackProviderIcon size={18} />
          <h2 className="font-body text-[14px] font-semibold text-base-black dark:text-white">{t('review.webhookNotificationsTitle')}</h2>
        </div>
        <p className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] mb-[12px]">
          {t('review.webhookProDescription')}
        </p>
        <span className="inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-[5px] bg-[#EFEFF1] dark:bg-[#28282B] text-[#818185] dark:text-[#A0A0A3] text-[12px] font-semibold">
          {t('review.webhookProFeature')}
        </span>
      </div>
    );
  }

  return (
    <div>
      {/* Provider switcher */}
      <div className="flex items-center gap-[6px] mb-[16px]">
        {(Object.values(PROVIDERS) as typeof PROVIDERS[ProviderKey][]).map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setActiveProvider(p.key)}
            style={activeProvider === p.key ? { backgroundColor: `${p.color}1A`, color: p.color } : {}}
            className={`inline-flex items-center gap-[6px] h-[32px] px-[12px] rounded-[32px] font-body text-[13px] font-medium transition-all ${
              activeProvider === p.key
                ? 'border border-current'
                : 'bg-[#EFEFF1] text-[#818185] hover:bg-[#E5E5ED]'
            }`}
          >
            <p.Icon size={14} color={activeProvider === p.key ? p.color : '#818185'} />
            {p.label}
          </button>
        ))}
      </div>

      <ProviderWebhookPanel key={activeProvider} slug={slug} provider={provider} />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
/*  Queue Features Section                                    */
/* ═══════════════════════════════════════════════════════════ */
const QUEUE_TOGGLE_LABELS: Record<string, string> = {
  chat: 'Queue Chat',
  qrCheckin: 'QR Check-in',
  waitEstimates: 'Wait Time Estimates',
  queuePasswords: 'Queue Passwords',
  privacyMode: 'Privacy Mode',
};

/* ═══════════════════════════════════════════════════════════ */
/*  Queue Settings Modal                                      */
/* ═══════════════════════════════════════════════════════════ */
type SettingsTab = 'features' | 'notifications' | 'admins' | 'template';

const QueueSettingsModal = ({
  slug, open, onClose, hasTeam, isPro, isCompany, featureToggles
}: {
  slug: string; open: boolean; onClose: () => void;
  hasTeam: boolean; isPro: boolean; isCompany: boolean;
  featureToggles?: Record<string, boolean> | null;
}) => {
  const [tab, setTab] = React.useState<SettingsTab>('features');

  if (!open) return null;

  const tabs: { key: SettingsTab; label: string; icon: React.ReactNode; show: boolean }[] = [
    { key: 'features', label: 'Features', show: true, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="5" width="22" height="14" rx="7" ry="7"/><circle cx="16" cy="12" r="3"/></svg> },
    { key: 'notifications', label: 'Notifications', show: true, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg> },
    { key: 'admins', label: 'Admins', show: true, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
    { key: 'template', label: 'Template', show: isCompany, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg> },
  ];

  const visibleTabs = tabs.filter(t => t.show);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[620px] h-[460px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-[24px] pt-[20px] pb-[12px] border-b border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.06)]">
          <h2 className="font-display text-[18px] font-bold text-base-black dark:text-white">Queue Settings</h2>
          <button onClick={onClose} className="w-[32px] h-[32px] rounded-full hover:bg-[#EFEFF1] dark:hover:bg-[#28282B] flex items-center justify-center transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Sidebar + Content layout */}
        <div className="flex flex-col md:flex-row flex-1 min-h-0">
          {/* Nav sidebar (horizontal icon row on mobile, vertical sidebar on desktop) */}
          <nav className="md:w-[180px] flex-shrink-0 border-b md:border-b-0 md:border-r border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.06)] flex md:flex-col md:py-[8px]">
            {visibleTabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`flex items-center justify-center md:justify-start gap-[10px] px-[16px] md:px-[20px] py-[12px] font-body text-[13px] font-medium transition-all text-left w-full ${
                  tab === t.key
                    ? 'bg-[#F0F0F2] dark:bg-[#28282B] text-base-black dark:text-white'
                    : 'text-[#818185] hover:bg-[#F7F7F8] dark:hover:bg-[#28282B]/50 hover:text-base-black dark:hover:text-white'
                }`}
              >
                <span className="w-[18px] h-[18px] flex items-center justify-center flex-shrink-0">{t.icon}</span>
                <span className="hidden md:inline">{t.label}</span>
              </button>
            ))}
          </nav>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-[24px]">
            {tab === 'features' && <SettingsFeatures slug={slug} featureToggles={featureToggles} isCompany={isCompany} isPro={isPro} />}
            {tab === 'notifications' && <WebhookNotificationsSection slug={slug} isPro={isPro} />}
            {tab === 'admins' && <AdminPanel slug={slug} hasTeam={hasTeam} isPro={isPro} />}
            {tab === 'template' && <SettingsTemplate slug={slug} />}
          </div>
        </div>
      </div>
    </div>
  );
};

/* Settings: Features tab content */
const SettingsFeatures = ({ slug, featureToggles, isCompany, isPro }: { slug: string; featureToggles?: Record<string, boolean> | null; isCompany: boolean; isPro: boolean }) => {
  const queryClient = useQueryClient();
  const toggles = { chat: true, qrCheckin: false, waitEstimates: true, queuePasswords: true, privacyMode: false, ...(featureToggles ?? {}) };

  const mutation = useMutation({
    mutationFn: (update: Record<string, boolean>) => reviewService.updateFeatureToggles(slug, update),
    onSuccess: () => { toast.success('Feature updated'); queryClient.invalidateQueries({ queryKey: ['review-poll', slug] }); },
    onError: () => toast.error('Failed to update feature'),
  });

  const visibleToggles = Object.entries(QUEUE_TOGGLE_LABELS).filter(([key]) => {
    if (key === 'privacyMode') return isPro || isCompany;
    if (key === 'qrCheckin' || key === 'waitEstimates') return isCompany;
    return true;
  });

  return (
    <div>
      <p className="font-body text-[12px] text-[#818185] dark:text-[#A0A0A3] mb-[16px]">Enable or disable features for this queue.</p>
      <div className="space-y-[10px]">
        {visibleToggles.map(([key, label]) => (
          <div key={key} className="flex items-center justify-between py-[6px]">
            <span className="font-body text-[13px] text-base-black dark:text-white">{label}</span>
            <button
              onClick={() => mutation.mutate({ [key]: !toggles[key] })}
              disabled={mutation.isPending}
              className={`relative w-[40px] h-[22px] rounded-full transition-colors ${
                toggles[key] ? 'bg-[#0ea5c9]' : 'bg-[#BFBFC0] dark:bg-[#3D3D40]'
              }`}
            >
              <span className={`absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white shadow transition-transform ${
                toggles[key] ? 'left-[20px]' : 'left-[2px]'
              }`} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* Settings: Template tab content */
const SettingsTemplate = ({ slug }: { slug: string }) => {
  const [name, setName] = React.useState('');

  const mutation = useMutation({
    mutationFn: () => reviewService.saveAsTemplate(slug, name.trim()),
    onSuccess: (data) => {
      toast.success(`Template "${data?.name}" saved`);
      setName('');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to save template'),
  });

  return (
    <div>
      <p className="font-body text-[12px] text-[#818185] dark:text-[#A0A0A3] mb-[16px]">
        Save this queue's settings as a reusable template for your company.
      </p>
      <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) mutation.mutate(); }} className="flex items-center gap-[8px]">
        <input
          type="text"
          placeholder="Template name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="flex-1 h-[40px] rounded-[24px] border border-[#BFBFC0] dark:border-[rgba(255,255,255,0.12)] dark:bg-[#28282B] dark:text-white dark:placeholder-[#6B6B6F] px-[14px] text-[13px] focus:outline-none focus:ring-2 focus:ring-[rgba(93,225,255,0.2)]"
        />
        <button
          type="submit"
          disabled={!name.trim() || mutation.isPending}
          className="h-[40px] px-[16px] bg-[#1D1D1E] dark:bg-[#28282B] text-white rounded-[32px] text-[13px] font-medium hover:bg-[#29292A] dark:hover:bg-[#333336] transition-all disabled:opacity-50"
        >
          {mutation.isPending ? 'Saving...' : 'Save Template'}
        </button>
      </form>
    </div>
  );
};
